import { MACROS, SETORES, UNIDADES } from "@caint/data";
import { EixoChip, MatSelect, PesoChip, RiskBadge } from "@caint/design-system";
import type { Momento, NivelMaturidade } from "@caint/engine";
import { ACCEPT_IMAGEM, ACCEPT_IMAGEM_PDF } from "@caint/shared";
import type { RespostaItem, TipoConstatacao } from "@caint/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AvaliacaoRemota, EvidenciaRemota } from "../../lib/api.ts";
import { engine } from "../../lib/engine.ts";
import { COR_MATURIDADE, RUBRICA_MATURIDADE } from "../../lib/escalaMaturidade.ts";
import type { EvidenciaPendente } from "../../persistence/evidenciaQueue.ts";
import type { ItemMerged } from "../../state/useAppState.ts";
import "./checklist.css";

type EnviarEvidencia = (itemId: string, momento: Momento, arquivo: File) => Promise<string | null>;
type ExcluirEvidencia = (ev: EvidenciaRemota) => Promise<string | null>;
type SubstituirEvidencia = (ev: EvidenciaRemota, arquivo: File) => Promise<string | null>;
type RemoverPendente = (localId: string) => Promise<void>;

export interface ChecklistDesktopProps {
  items: ItemMerged[];
  momento: Momento;
  setMomento: (m: Momento) => void;
  patch: (itemId: string, patch: Partial<RespostaItem>) => void;
  toast: (msg: string) => void;
  evidencias: Record<string, EvidenciaRemota[]>;
  evidenciasPendentes: Record<string, EvidenciaPendente[]>;
  enviarEvidencia: EnviarEvidencia;
  excluirEvidencia: ExcluirEvidencia;
  substituirEvidencia: SubstituirEvidencia;
  removerEvidenciaPendente: RemoverPendente;
  editavel: boolean;
  avaliacaoAtual?: AvaliacaoRemota;
  /** Setores ativos no escopo (recorte do checklist; doc 17b §3.3). */
  setoresAtivos: readonly string[];
  /** Reabre a tela de escopo (Setores da Unidade). */
  onEditarEscopo?: () => void;
}

function nomeUnidade(unidadeId?: string): string {
  return UNIDADES.find((u) => u.id === unidadeId)?.nome ?? "Unidade";
}

// Rubrica e cores da curva de maturidade vêm de `lib/escalaMaturidade` — fonte
// única, compartilhada com o Checklist mobile, o Plano de Ação e a impressão.

/** Tipos de constatação (técnica de auditoria) e o que cada um pede no registro. */
const TIPOS_CONSTATACAO: { v: TipoConstatacao; rotulo: string; ajuda: string }[] = [
  {
    v: "documento",
    rotulo: "📄 Documento/registro existe",
    ajuda: "Cite/identifique o documento (e anexe, se possível).",
  },
  { v: "observacao", rotulo: "👁️ Observação direta", ajuda: "Descreva o que foi observado in loco." },
  {
    v: "declaracao",
    rotulo: "💬 Declaração (entrevista)",
    ajuda: "Resuma o que foi informado e por quem (cargo).",
  },
  {
    v: "ausencia",
    rotulo: "🚫 Ausência constatada",
    ajuda: "Diga o que foi procurado, onde, e quem confirmou a ausência.",
  },
];
/** Constatações sem objeto físico → pedem a fonte (cargo de quem confirmou). */
const PEDE_FONTE = new Set<TipoConstatacao>(["declaracao", "ausencia"]);

function eviKey(m: Momento): "evidenciaMi" | "evidenciaMf" {
  return m === "mi" ? "evidenciaMi" : "evidenciaMf";
}
function tipoKey(m: Momento): "tipoMi" | "tipoMf" {
  return m === "mi" ? "tipoMi" : "tipoMf";
}
function fonteKey(m: Momento): "fonteMi" | "fonteMf" {
  return m === "mi" ? "fonteMi" : "fonteMf";
}
function matDe(item: ItemMerged, m: Momento): NivelMaturidade | null {
  return item[m] ?? null;
}
function semEvidencia(item: ItemMerged, m: Momento): boolean {
  const mat = matDe(item, m);
  return mat != null && mat !== "na" && (item[eviKey(m)] ?? "").trim() === "";
}

/** Legenda da escala (rubrica ancorada) — réplica do DSLegenda do protótipo. */
function DSLegenda({ mat }: { mat: NivelMaturidade | null }) {
  const niveis: NivelMaturidade[] = ["0", "1", "2", "3", "4", "na"];
  return (
    <div style={{ marginTop: "var(--space-3)" }}>
      <div
        style={{
          fontSize: "var(--text-2xs)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--text-faint)",
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        Legenda da escala (rubrica ancorada)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        {niveis.map((k) => {
          const on = mat === k;
          return (
            <div
              key={k}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                padding: "8px 9px",
                borderRadius: "var(--radius-sm)",
                background: on ? "var(--surface-2)" : "transparent",
                border: on ? "1px solid var(--border-strong)" : "1px solid var(--border)",
              }}
            >
              <span
                style={{
                  flex: "0 0 auto",
                  width: 20,
                  height: 20,
                  borderRadius: 5,
                  background: COR_MATURIDADE[k],
                  color: "#fff",
                  fontFamily: "var(--font-mono)",
                  fontWeight: 800,
                  fontSize: 11,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {k === "na" ? "NA" : k}
              </span>
              <span
                style={{
                  fontSize: "var(--text-2xs)",
                  lineHeight: 1.32,
                  color: on ? "var(--text)" : "var(--text-muted)",
                }}
              >
                <b style={{ color: on ? "var(--text)" : "var(--slate-600)" }}>{engine.situacao(k)}</b> —{" "}
                {RUBRICA_MATURIDADE[k]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PreviewLocal {
  localId: string;
  url: string;
  img: boolean;
  /** Motivo da recusa do servidor, quando houve (ver `EvidenciaPendente.erro`). */
  erro?: string;
}

const ESTILO_MINI: React.CSSProperties = {
  aspectRatio: "1",
  borderRadius: 8,
  overflow: "hidden",
  background: "var(--surface-2)",
};

const ACAO_FOTO: React.CSSProperties = {
  flex: 1,
  border: "none",
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  fontSize: 12,
  lineHeight: 1,
  padding: "3px 0",
  cursor: "pointer",
};

/**
 * Galeria de evidências: enviadas (Ver + Substituir + Excluir) + pendentes na
 * fila (⏳, preview local, com cancelar). As ações só aparecem com ciclo editável.
 */
function Galeria({
  evis,
  previews,
  editavel,
  enviando,
  excluindo,
  onSubstituir,
  onExcluir,
  onCancelarPendente,
}: {
  evis: EvidenciaRemota[];
  previews: PreviewLocal[];
  editavel: boolean;
  enviando: boolean;
  excluindo: string | null;
  onSubstituir: (ev: EvidenciaRemota) => void;
  onExcluir: (ev: EvidenciaRemota) => void;
  onCancelarPendente: (localId: string) => void;
}) {
  if (evis.length === 0 && previews.length === 0) {
    return null;
  }
  return (
    <div className="row-wrap" style={{ gap: 8, marginTop: 10 }}>
      {evis.map((ev) => {
        const removendo = excluindo === ev.id;
        return (
          <div
            key={ev.id}
            style={{
              ...ESTILO_MINI,
              width: 60,
              height: 60,
              position: "relative",
              border: "1px solid var(--border)",
              opacity: removendo ? 0.5 : 1,
            }}
          >
            <a
              href={ev.url}
              target="_blank"
              rel="noopener noreferrer"
              title="Ver em tamanho real"
              style={{ display: "block", width: "100%", height: "100%" }}
            >
              {ev.contentType.startsWith("image/") ? (
                <img
                  // Miniatura quando existe; sem ela, a foto cheia (ver mobile).
                  src={ev.urlMiniatura ?? ev.url}
                  alt="evidência"
                  loading="lazy"
                  decoding="async"
                  width={60}
                  height={60}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span style={{ display: "grid", placeItems: "center", height: "100%", fontSize: 11 }}>
                  PDF
                </span>
              )}
            </a>
            {editavel && (
              <div style={{ position: "absolute", inset: "auto 0 0 0", display: "flex" }}>
                <button
                  type="button"
                  title="Substituir esta foto"
                  aria-label="Substituir foto"
                  disabled={enviando || removendo}
                  onClick={() => onSubstituir(ev)}
                  style={ACAO_FOTO}
                >
                  🔁
                </button>
                <button
                  type="button"
                  title="Excluir esta foto"
                  aria-label="Excluir foto"
                  disabled={removendo}
                  onClick={() => onExcluir(ev)}
                  style={{ ...ACAO_FOTO, borderLeft: "1px solid rgba(255,255,255,0.25)" }}
                >
                  {removendo ? "…" : "🗑"}
                </button>
              </div>
            )}
          </div>
        );
      })}
      {previews.map((p) => (
        <div
          key={p.localId}
          style={{
            ...ESTILO_MINI,
            width: 60,
            height: 60,
            position: "relative",
            border: `1px dashed var(${p.erro ? "--danger" : "--warning"})`,
          }}
          title={p.erro ?? "Na fila — sobe sozinha ao reconectar."}
        >
          {p.img ? (
            <img
              src={p.url}
              alt="pendente"
              style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }}
            />
          ) : (
            <span style={{ display: "grid", placeItems: "center", height: "100%", fontSize: 11 }}>PDF</span>
          )}
          <span
            style={{
              position: "absolute",
              inset: "auto 0 0 0",
              background: `var(${p.erro ? "--danger" : "--warning"})`,
              color: "#fff",
              fontSize: 9,
              textAlign: "center",
            }}
          >
            {p.erro ? "⚠ recusada" : "⏳ na fila"}
          </span>
          {editavel && (
            <button
              type="button"
              title="Cancelar esta captura"
              aria-label="Cancelar captura"
              onClick={() => onCancelarPendente(p.localId)}
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                width: 18,
                height: 18,
                borderRadius: "50%",
                border: "none",
                background: "rgba(0,0,0,0.6)",
                color: "#fff",
                fontSize: 10,
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Lastro da nota: tipo de constatação (mantido) + evidência verificada (textual)
 * + fonte (quando sem objeto físico) + anexos reais (foto/PDF via fila). Estilo
 * fiel ao protótipo. A Efetividade do controle foi removida — não é reintroduzida.
 */
function EvidenciaBloco({
  item,
  momento,
  mat,
  needsEvi,
  editavel,
  patch,
  evis,
  pendentes,
  enviarEvidencia,
  excluirEvidencia,
  substituirEvidencia,
  removerEvidenciaPendente,
  toast,
}: {
  item: ItemMerged;
  momento: Momento;
  mat: NivelMaturidade | null;
  needsEvi: boolean;
  editavel: boolean;
  patch: (itemId: string, patch: Partial<RespostaItem>) => void;
  evis: EvidenciaRemota[];
  pendentes: EvidenciaPendente[];
  enviarEvidencia: EnviarEvidencia;
  excluirEvidencia: ExcluirEvidencia;
  substituirEvidencia: SubstituirEvidencia;
  removerEvidenciaPendente: RemoverPendente;
  toast: (msg: string) => void;
}) {
  const evi = item[eviKey(momento)] ?? "";
  const tipo = (item[tipoKey(momento)] ?? null) as TipoConstatacao | null;
  const fonte = item[fonteKey(momento)] ?? "";
  const tipoMeta = TIPOS_CONSTATACAO.find((t) => t.v === tipo);
  const camRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const subRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  // Foto que será trocada quando o próximo arquivo do subRef chegar.
  const substituindoRef = useRef<EvidenciaRemota | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);

  const previews = useMemo(
    () =>
      pendentes.map((p) => ({
        localId: p.localId,
        url: URL.createObjectURL(p.blob),
        img: p.contentType.startsWith("image/"),
        erro: p.erro,
      })),
    [pendentes],
  );
  useEffect(
    () => () => {
      for (const p of previews) {
        URL.revokeObjectURL(p.url);
      }
    },
    [previews],
  );

  const aoEscolher = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivos = [...(e.target.files ?? [])];
    e.target.value = "";
    if (arquivos.length === 0) {
      return;
    }
    setEnviando(true);
    let erro: string | null = null;
    for (const arquivo of arquivos) {
      erro = (await enviarEvidencia(item.id, momento, arquivo)) ?? erro;
    }
    setEnviando(false);
    toast(erro ?? "Evidência anexada ✓");
  };

  const pedirSubstituicao = (ev: EvidenciaRemota) => {
    substituindoRef.current = ev;
    subRef.current?.click();
  };

  const aoSubstituir = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    e.target.value = "";
    const alvo = substituindoRef.current;
    substituindoRef.current = null;
    if (!arquivo || !alvo) {
      return;
    }
    setEnviando(true);
    const erro = await substituirEvidencia(alvo, arquivo);
    setEnviando(false);
    toast(erro ?? "Foto substituída ✓");
  };

  const aoExcluir = async (ev: EvidenciaRemota) => {
    if (!confirm("Excluir esta foto? A ação registra quem removeu (auditoria).")) {
      return;
    }
    setExcluindo(ev.id);
    const erro = await excluirEvidencia(ev);
    setExcluindo(null);
    toast(erro ?? "Foto removida ✓");
  };

  const aoCancelarPendente = async (localId: string) => {
    await removerEvidenciaPendente(localId);
    toast("Captura cancelada ✓");
  };

  return (
    <div style={{ marginTop: "var(--space-4)" }}>
      {mat != null && mat !== "na" && (
        <div className="field" style={{ marginBottom: "var(--space-3)" }}>
          <label className="label" htmlFor={`tipo-${item.id}`}>
            Tipo de constatação
          </label>
          <select
            id={`tipo-${item.id}`}
            className="select"
            value={tipo ?? ""}
            disabled={!editavel}
            onChange={(e) =>
              patch(item.id, { [tipoKey(momento)]: (e.target.value || null) as TipoConstatacao | null })
            }
          >
            <option value="">Selecione como a nota foi constatada…</option>
            {TIPOS_CONSTATACAO.map((t) => (
              <option key={t.v} value={t.v}>
                {t.rotulo}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor={`evi-${item.id}`}>
          Evidência verificada {needsEvi && <span className="required">· obrigatória</span>}
        </label>
        <textarea
          id={`evi-${item.id}`}
          className={`evidence-input${needsEvi ? " missing" : ""}`}
          value={evi}
          disabled={!editavel}
          placeholder={
            tipoMeta?.ajuda ?? "Registre a evidência observada em campo (documento, observação ou ausência)…"
          }
          onChange={(e) => patch(item.id, { [eviKey(momento)]: e.target.value })}
        />

        <div className="row-wrap" style={{ gap: 8, marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={enviando || !editavel}
            onClick={() => camRef.current?.click()}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>{" "}
            {enviando ? "Enviando…" : "Tirar foto"}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={enviando || !editavel}
            onClick={() => fileRef.current?.click()}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>{" "}
            Anexar arquivo
          </button>
        </div>
        {/* `accept` explícito (não `image/*`) faz o iOS converter HEIC→JPEG sozinho. */}
        <input
          ref={camRef}
          type="file"
          accept={ACCEPT_IMAGEM}
          capture="environment"
          hidden
          onChange={aoEscolher}
        />
        <input ref={fileRef} type="file" accept={ACCEPT_IMAGEM_PDF} multiple hidden onChange={aoEscolher} />
        {/* Input dedicado da substituição (arquivo único, sem `multiple`). */}
        <input ref={subRef} type="file" accept={ACCEPT_IMAGEM_PDF} hidden onChange={aoSubstituir} />

        <Galeria
          evis={evis}
          previews={previews}
          editavel={editavel}
          enviando={enviando}
          excluindo={excluindo}
          onSubstituir={pedirSubstituicao}
          onExcluir={aoExcluir}
          onCancelarPendente={aoCancelarPendente}
        />
      </div>

      {tipo != null && PEDE_FONTE.has(tipo) && (
        <div className="field" style={{ marginTop: "var(--space-3)" }}>
          <label className="label" htmlFor={`fonte-${item.id}`}>
            Fonte — cargo de quem confirmou
          </label>
          <input
            id={`fonte-${item.id}`}
            className="input"
            value={fonte}
            disabled={!editavel}
            placeholder="Ex.: Enfermeiro(a) responsável do setor"
            onChange={(e) => patch(item.id, { [fonteKey(momento)]: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

/** Item do checklist em card inline (réplica do ChecklistItem do protótipo). */
function ChecklistItem({
  item,
  momento,
  patch,
  editavel,
  evis,
  pendentes,
  enviarEvidencia,
  excluirEvidencia,
  substituirEvidencia,
  removerEvidenciaPendente,
  toast,
}: {
  item: ItemMerged;
  momento: Momento;
  patch: (itemId: string, patch: Partial<RespostaItem>) => void;
  editavel: boolean;
  evis: EvidenciaRemota[];
  pendentes: EvidenciaPendente[];
  enviarEvidencia: EnviarEvidencia;
  excluirEvidencia: ExcluirEvidencia;
  substituirEvidencia: SubstituirEvidencia;
  removerEvidenciaPendente: RemoverPendente;
  toast: (msg: string) => void;
}) {
  const mat = matDe(item, momento);
  const score = engine.score(mat);
  const resid = engine.riscoResidual(item, momento);
  const nivel = resid == null ? null : engine.nivelRisco(resid);
  const lc = nivel ? nivel.toLowerCase() : "rp";
  const inh = engine.riscoInerente(item);
  const needsEvi = semEvidencia(item, momento);
  const coreAlert = item.peso === 3 && (mat === "0" || mat === "1");
  const conf = mat != null && mat !== "na" ? ` · ${engine.conformidade(mat)}` : "";
  const scoreTxt = typeof score === "number" ? engine.num2(score) : score === "NA" ? "NA" : "—";

  return (
    <div className="checkitem" style={{ marginBottom: "var(--space-4)" }}>
      <div>
        <div className="checkitem-head">
          <span className="chip chip-code">{item.code}</span>
          <PesoChip peso={item.peso} />
          <EixoChip eixo={item.eixo} />
          <span className="chip" style={{ fontSize: "var(--text-2xs)" }}>
            {item.processo}
          </span>
          {coreAlert && (
            <span className="flag" style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>
              ⛔ CORE crítico
            </span>
          )}
          {mat != null && mat !== "na" && (
            <span
              className="flag"
              style={
                needsEvi
                  ? {
                      background: "var(--warning-soft)",
                      color: "color-mix(in srgb, var(--warning) 72%, black)",
                    }
                  : { background: "var(--mat-3-soft)", color: "var(--mat-4)" }
              }
            >
              {needsEvi ? "⚠ sem evidência" : "✓ evidência"}
            </span>
          )}
          {mat == null && (item[eviKey(momento)] ?? "").trim() !== "" && (
            <span
              className="flag"
              style={{
                background: "var(--warning-soft)",
                color: "color-mix(in srgb, var(--warning) 72%, black)",
              }}
            >
              ⚠ falta a nota
            </span>
          )}
        </div>
        <div className="checkitem-q">{item.pergunta}</div>
        <div className="checkitem-meta">
          <span>
            <b>Norma:</b> {item.norma}
          </span>
          <span>
            <b>Evidência sugerida:</b> {item.evidencia}
          </span>
        </div>

        {!editavel && (
          <div
            className="alert"
            style={{
              marginTop: "var(--space-4)",
              background: "var(--surface-2)",
              borderColor: "var(--border)",
              borderLeftColor: "var(--border)",
              color: "var(--text-muted)",
              fontSize: "var(--text-sm)",
            }}
          >
            👁️ Somente leitura — sem ciclo aberto para esta unidade.
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "var(--space-5)",
            flexWrap: "wrap",
            marginTop: "var(--space-4)",
            alignItems: "flex-start",
          }}
        >
          <div className="field" style={{ flex: "0 0 auto" }}>
            <span className="label">Maturidade observada</span>
            <div style={{ pointerEvents: editavel ? "auto" : "none", opacity: editavel ? 1 : 0.6 }}>
              <MatSelect value={mat} onChange={(v) => patch(item.id, { [momento]: v })} />
            </div>
          </div>
        </div>

        <DSLegenda mat={mat} />

        <EvidenciaBloco
          item={item}
          momento={momento}
          mat={mat}
          needsEvi={needsEvi}
          editavel={editavel}
          patch={patch}
          evis={evis}
          pendentes={pendentes}
          enviarEvidencia={enviarEvidencia}
          excluirEvidencia={excluirEvidencia}
          substituirEvidencia={substituirEvidencia}
          removerEvidenciaPendente={removerEvidenciaPendente}
          toast={toast}
        />
      </div>

      <div className="checkitem-side">
        <div className="risk-readout">
          <span className="rr-label">Situação · conformidade</span>
          <span style={{ fontWeight: 700, fontSize: "var(--text-base)" }}>
            {engine.situacao(mat) || "—"}
            {conf}
          </span>
        </div>
        <div className="row-wrap" style={{ gap: 14 }}>
          <div className="risk-readout">
            <span className="rr-label">Score</span>
            <span className="rr-val">{scoreTxt}</span>
          </div>
          <div className="risk-readout">
            <span className="rr-label">P×I inerente</span>
            <span className="rr-val">{inh}</span>
          </div>
        </div>
        <div className="risk-readout">
          <span className="rr-label">Risco residual ({momento === "mi" ? "inicial" : "final"})</span>
          <div className="row-wrap" style={{ gap: 8, alignItems: "baseline" }}>
            <span
              className="rr-val"
              style={{ color: resid == null ? "var(--text-faint)" : `var(--risk-${lc})` }}
            >
              {resid == null ? "—" : engine.num1(resid)}
            </span>
            {resid != null && <RiskBadge value={resid} />}
          </div>
        </div>
        {resid != null && resid >= 8 && (
          <div className="alert alert-warning" style={{ padding: "9px 11px" }}>
            <span className="alert-ico">⚠️</span>
            <div className="alert-body">
              <div className="alert-text" style={{ fontSize: "var(--text-xs)" }}>
                Risco ≥ 8 → prioridade no Plano de Ação ({nivel === "RC" ? "15 dias" : "60 dias"}).
              </div>
            </div>
          </div>
        )}
        {needsEvi && (
          <div className="alert alert-danger" style={{ padding: "9px 11px" }}>
            <span className="alert-ico">✕</span>
            <div className="alert-body">
              <div className="alert-text" style={{ fontSize: "var(--text-xs)" }}>
                Nota sem evidência não é válida (regra de ouro).
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ChecklistDesktop(props: ChecklistDesktopProps) {
  const {
    items,
    momento,
    setMomento,
    patch,
    toast,
    evidencias,
    evidenciasPendentes,
    enviarEvidencia,
    excluirEvidencia,
    substituirEvidencia,
    removerEvidenciaPendente,
    editavel,
    avaliacaoAtual,
    setoresAtivos,
    onEditarEscopo,
  } = props;

  // Recorte por escopo (doc 17b §3.3): só setores ATIVOS são navegáveis; os
  // inativos aparecem apagados ("fora de escopo") e não entram no cálculo.
  const ativos = useMemo(() => new Set(setoresAtivos), [setoresAtivos]);
  const comItens = useMemo(() => SETORES.filter((s) => ativos.has(s.code)), [ativos]);
  const inativos = useMemo(() => SETORES.filter((s) => !ativos.has(s.code)), [ativos]);
  const [setor, setSetor] = useState<string>(comItens[0]?.code ?? "");

  // Se o setor selecionado saiu do escopo (ex.: escopo editado), volta ao 1º ativo.
  useEffect(() => {
    if (setor && !ativos.has(setor)) {
      setSetor(comItens[0]?.code ?? "");
    }
  }, [ativos, setor, comItens]);

  const cur = items.filter((it) => it.sector === setor);
  const meta = SETORES.find((s) => s.code === setor);
  const secScore = engine.setorScore(cur, momento);
  const core0 = engine.contarCore(cur, 0, momento);
  const core1 = engine.contarCore(cur, 1, momento);
  const nc = engine.contarConf(cur, "NC", momento);
  const na = engine.contarNA(cur, momento);
  const preench = cur.filter((it) => it[momento] != null).length;

  return (
    <div className="dk-page dk-check" style={{ maxWidth: 1320 }}>
      <div className="page-head between">
        <div>
          <div className="page-eyebrow">Checklist setorial · {nomeUnidade(avaliacaoAtual?.unidadeId)}</div>
          <h1 className="page-title">Coleta em campo</h1>
          <p className="page-sub">
            {editavel
              ? "Atribua um único número de maturidade — score, conformidade e risco residual recalculam ao vivo."
              : "👁️ Somente leitura — sem ciclo aberto para esta unidade."}
          </p>
        </div>
        <div className="row-wrap" style={{ gap: 10 }}>
          {onEditarEscopo && (
            <button type="button" className="m-btn" onClick={onEditarEscopo}>
              Editar setores
            </button>
          )}
          <div className="momento-toggle">
            <button type="button" className={momento === "mi" ? "on" : ""} onClick={() => setMomento("mi")}>
              1ª visita · Jun
            </button>
            <button type="button" className={momento === "mf" ? "on" : ""} onClick={() => setMomento("mf")}>
              2ª visita · Nov
            </button>
          </div>
        </div>
      </div>

      <div className="check-layout">
        {/* nav de setores */}
        <div
          className="card card-pad"
          style={{ position: "sticky", top: 16, maxHeight: "calc(100dvh - 32px)", overflowY: "auto" }}
        >
          <div className="panel-label">{comItens.length} setores</div>
          <div className="sec-list">
            {comItens.map((s) => {
              const its = items.filter((it) => it.sector === s.code);
              const sc = its.length ? engine.setorScore(its, momento) : null;
              const has = its.length > 0;
              const clr =
                sc == null
                  ? "var(--surface-2)"
                  : sc < 0.46
                    ? "var(--danger-soft)"
                    : sc < 0.6
                      ? "var(--mat-2-soft)"
                      : "var(--mat-3-soft)";
              const txt = sc == null ? "var(--text-faint)" : sc < 0.46 ? "var(--danger)" : "var(--slate-700)";
              return (
                <button
                  key={s.code}
                  type="button"
                  className={`sec-btn${setor === s.code ? " active" : ""}`}
                  onClick={() => setSetor(s.code)}
                >
                  <span className="sc">{s.code}</span>
                  <span className="sn">{s.nome}</span>
                  <span className="sec-score" style={{ background: clr, color: txt }}>
                    {has ? engine.num2(sc) : "—"}
                  </span>
                </button>
              );
            })}
            {inativos.length > 0 && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                <div className="panel-label" style={{ marginBottom: 6 }}>
                  Fora do escopo
                </div>
                {inativos.map((s) => (
                  <div key={s.code} className="sec-out" title="Setor não existente nesta unidade">
                    <span className="sc">{s.code}</span>
                    <span className="sn">{s.nome}</span>
                    <span
                      className="sec-score"
                      style={{ background: "var(--surface-2)", color: "var(--text-faint)" }}
                    >
                      —
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* itens */}
        <div>
          {meta && (
            <div className="card card-pad" style={{ marginBottom: "var(--space-4)" }}>
              <div className="between" style={{ flexWrap: "wrap", gap: "var(--space-4)" }}>
                <div>
                  <div style={{ fontSize: "var(--text-lg)", fontWeight: 800, letterSpacing: "-0.02em" }}>
                    {meta.code} · {meta.nome}
                  </div>
                  <div className="page-sub" style={{ marginTop: 2 }}>
                    Macro {meta.macro} · {MACROS[meta.macro]} · {meta.itens} itens no instrumento
                  </div>
                </div>
                <div className="row-wrap" style={{ gap: "var(--space-5)" }}>
                  <div style={{ textAlign: "right" }}>
                    <div className="spec">Score do setor</div>
                    <b
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "var(--text-2xl)",
                        fontWeight: 800,
                        color: secScore != null && secScore < 0.46 ? "var(--danger)" : "var(--primary)",
                      }}
                    >
                      {secScore == null ? "—" : engine.num2(secScore)}
                    </b>
                  </div>
                </div>
              </div>
              <div className="row-wrap" style={{ marginTop: "var(--space-4)", gap: 10 }}>
                <span className="chip" style={{ fontSize: "var(--text-2xs)" }}>
                  {preench}/{cur.length} preenchidos
                </span>
                {core0 + core1 > 0 && (
                  <span className="flag" style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>
                    ⛔ {core0 + core1} CORE em Mat 0/1
                  </span>
                )}
                {nc > 0 && (
                  <span className="flag" style={{ background: "var(--mat-0-soft)", color: "var(--mat-0)" }}>
                    {nc} NC
                  </span>
                )}
                {na > 0 && (
                  <span
                    className="flag"
                    style={{ background: "var(--surface-2)", color: "var(--text-muted)" }}
                  >
                    {na} NA
                  </span>
                )}
              </div>
            </div>
          )}

          {cur.length === 0 ? (
            <div className="card card-pad" style={{ textAlign: "center", padding: "var(--space-16)" }}>
              <div style={{ fontWeight: 700 }}>Selecione um setor</div>
              <p className="page-sub" style={{ maxWidth: "44ch", margin: "6px auto 0" }}>
                Escolha um setor à esquerda para coletar a maturidade dos itens.
              </p>
            </div>
          ) : (
            cur.map((it) => (
              <ChecklistItem
                key={it.id}
                item={it}
                momento={momento}
                patch={patch}
                editavel={editavel}
                evis={evidencias[it.id] ?? []}
                pendentes={evidenciasPendentes[it.id] ?? []}
                enviarEvidencia={enviarEvidencia}
                excluirEvidencia={excluirEvidencia}
                substituirEvidencia={substituirEvidencia}
                removerEvidenciaPendente={removerEvidenciaPendente}
                toast={toast}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
